import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { AuthStore, User, UserOrganization } from './auth.interface';
import {
  adminGetUsers,
  adminInviteUser,
  adminRemoveUser,
  adminUpdateUser,
  updateUser,
  verifyUser,
  changePassword as changePasswordAPI,
} from './auth.api';
import { createSelectors } from '@/libs/storeUtil';
import { useMemo } from 'react';
import { useConfigStore } from '../config/config.store';
import { ERequestStatus } from '@/libs/utils';
import { toast } from '@/components/toast/use-toast';
export const useAuthStore = create<AuthStore>()(
  devtools(
    persist(
      (set, get) => ({
        user: null,
        isAuthenticated: false,
        userOrganizations: [],

        setUser: (state: User) => set({ user: state }),
        verify: async () => {
          try {
            const user = await verifyUser({});
            // if (
            //   user.status === "inactive" &&
            //   window.location.pathname !== "/verification"
            // ) {
            //   window.location.href = "/verification";
            //   set({ isAuthenticated: true, user });
            //   return;
            // }

            if (
              user.status === 'onboarding' &&
              window.location.pathname !== '/onboarding' &&
              window.location.pathname !== '/select-workspace'
            ) {
              if (user.user_organizations?.length > 0) {
                await updateUser({
                  status: 'active',
                });
                window.location.href = '/select-workspace';
              } else {
                window.location.href = '/onboarding';
              }
              set({ isAuthenticated: true, user });
              return;
            }

            const org_id = localStorage.getItem('org_id');

            if (user.user_organizations?.length > 0) {
              if (!org_id) {
                if (window.location.pathname !== '/select-workspace') window.location.href = '/select-workspace';
              } else {
                const org = user.user_organizations.find(
                  (org: UserOrganization) => org.organization_id.toString() === org_id
                );
                if (!org) {
                  localStorage.removeItem('org_id');
                  if (user.user_organizations.length > 0) {
                    if (window.location.pathname !== '/select-workspace') window.location.href = '/select-workspace';
                  } else {
                    if (window.location.pathname !== '/onboarding') window.location.href = '/onboarding';
                  }
                }
              }
            } else {
              localStorage.removeItem('org_id');
            }
            set({ user, isAuthenticated: true });
            // get().getSubscription();
          } catch {
            set({ isAuthenticated: false });
            window.location.href = '/login';
          }
        },

        usersWorkspace: [],
        adminGetUsersWorkspace: async () => {
          const users = await adminGetUsers({});
          set({ usersWorkspace: users });
        },

        updateUserWorkspace: async (id: number, data: any) => {
          try {
            const user = get().user;
            if (user) {
              const updatedOrganizations = user.user_organizations.map(org =>
                org.organization.id === id
                  ? {
                      ...org,
                      organization: {
                        ...org.organization,
                        name: data.name,
                        description: data.description,
                        data: data.data,
                      },
                    }
                  : org
              );
              set({ user: { ...user, user_organizations: updatedOrganizations } });
            }
          } catch (error: any) {
            toast({
              variant: 'destructive',
              title: 'Failed to update workspace',
              description: error.message,
            });
          }
        },

        adminRemoveUserStatus: ERequestStatus.IDLE,
        adminRemoveUser: async (id: number) => {
          try {
            set({ adminRemoveUserStatus: ERequestStatus.LOADING });
            await adminRemoveUser(id);
            set({ adminRemoveUserStatus: ERequestStatus.SUCCESS });
            toast({
              variant: 'success',
              title: 'User removed successfully',
            });
            await get().adminGetUsersWorkspace();
          } catch (error: any) {
            set({ adminRemoveUserStatus: ERequestStatus.FAILED });
            toast({
              variant: 'destructive',
              title: 'Failed to remove user',
              description: error.message,
            });
          }
        },

        adminUpdateUserStatus: ERequestStatus.IDLE,
        adminUpdateUser: async (id: number, data: any) => {
          try {
            set({ adminUpdateUserStatus: ERequestStatus.LOADING });
            await adminUpdateUser({ id, role: data.role });
            set({ adminUpdateUserStatus: ERequestStatus.SUCCESS });
            toast({
              variant: 'success',
              title: 'User updated successfully',
            });
            await get().adminGetUsersWorkspace();
          } catch (error: any) {
            set({ adminUpdateUserStatus: ERequestStatus.FAILED });
            toast({
              variant: 'destructive',
              title: 'Failed to update user',
              description: error?.detail?.detail,
            });
          }
        },

        adminInviteUserStatus: ERequestStatus.IDLE,
        adminInviteUser: async (data: { email: string; role: string }) => {
          try {
            set({ adminInviteUserStatus: ERequestStatus.LOADING });
            await adminInviteUser(data);
            set({ adminInviteUserStatus: ERequestStatus.SUCCESS });
            toast({
              variant: 'success',
              title: 'User invited successfully',
            });
            await get().adminGetUsersWorkspace();
          } catch (error: any) {
            set({ adminInviteUserStatus: ERequestStatus.FAILED });
            toast({
              variant: 'destructive',
              title: 'Failed to invite user',
              description: error.message,
            });
          }
        },

        changePasswordStatus: ERequestStatus.IDLE,
        changePassword: async (data: { currentPassword: string; newPassword: string }) => {
          try {
            set({ changePasswordStatus: ERequestStatus.LOADING });
            await changePasswordAPI(data);
            set({ changePasswordStatus: ERequestStatus.SUCCESS });
          } catch (error: any) {
            set({ changePasswordStatus: ERequestStatus.FAILED });

            // Extract error message properly
            let errorMessage = 'Current password is incorrect';

            if (error?.response?.data?.detail) {
              if (typeof error.response.data.detail === 'string') {
                errorMessage = error.response.data.detail;
              } else if (typeof error.response.data.detail === 'object') {
                // Handle object response (e.g., validation errors)
                errorMessage = JSON.stringify(error.response.data.detail);
              }
            } else if (error?.detail) {
              if (typeof error.detail === 'string') {
                errorMessage = error.detail;
              } else if (typeof error.detail === 'object') {
                errorMessage = JSON.stringify(error.detail);
              }
            } else if (error?.message) {
              errorMessage = error.message;
            }

            toast({
              variant: 'destructive',
              title: 'Failed to change password',
              description: errorMessage,
            });
          }
        },

        resetChangePasswordStatus: () => {
          set({ changePasswordStatus: ERequestStatus.IDLE });
        },
      }),
      {
        name: 'auth-storage',
        storage: {
          getItem: name => {
            const str = sessionStorage.getItem(name);
            if (!str) return null;
            return JSON.parse(str);
          },
          setItem: (name, value) => {
            sessionStorage.setItem(name, JSON.stringify(value));
          },
          removeItem: name => {
            sessionStorage.removeItem(name);
          },
        },
      }
    )
  )
);

export const useUserStore = createSelectors(useAuthStore);

export const useUserOrganization = () => {
  const user = useUserStore.use.user();
  const config = useConfigStore();

  const currentOrganization = useMemo(() => {
    let currentOrg = localStorage.getItem('org_id');
    if (config.authentication === 'false') {
      currentOrg = '1';
    }
    const userOrgs = user?.user_organizations;

    if (!currentOrg) {
      return null;
    } else {
      return userOrgs?.find(
        org => org.organization_id.toString() === currentOrg || org.organization.organization_id === currentOrg
      );
    }
  }, [user, config, localStorage.getItem('org_id')]);

  return {
    userOrganizations: user?.user_organizations,
    currentOrganization,
  };
};
